/**
 * Labelpresets voor de QR-PDF.
 *
 * Spec: docs/superpowers/specs/2026-08-21-qr-traceability-sleutels-dragers-design.md §5
 *
 * Alleen de SLEUTELS en hun zichtbare labels staan hier, zodat het Zod-schema en
 * de backoffice-dropdown dezelfde lijst gebruiken. De geometrie (paginamaat,
 * raster, marges) hoort bij de renderer en staat in
 * `apps/api/src/lib/label-presets.ts`; een drift-guard-test bewaakt dat elke
 * sleutel hier ook geometrie heeft.
 */
import { z } from 'zod'
// Type-only import: qr-labels.ts <-> voorraad-uniek.ts is anders een
// runtime-cycle (voorraad-uniek importeert LABEL_PRESET_KEYS hieruit).
import type { VoorraadUniekStatus } from './voorraad-uniek'

export const LABEL_PRESET_KEYS = [
  'a4-24',
  'a4-65',
  'a4-sleutel',
  'rol-62x29',
  'rol-29x25',
  'rol-29x15',
] as const

export type LabelPresetKey = (typeof LABEL_PRESET_KEYS)[number]

// De rol-presets noemen de ROL in de naam: de Brother QL weigert een printjob
// waarvan het papierformaat niet bij de geplaatste rol hoort ("verkeerde
// formaat"), dus de gebruiker moet al bij het kiezen zien welke rol erbij
// hoort. 'rol-29x25' verving 'rol-25x25': 25 mm is geen bestaande DK-rolbreedte,
// het fysieke label is een stuk 29 mm-rol (DK-22211) van 25 mm lang.
export const LABEL_PRESET_LABELS: Record<LabelPresetKey, string> = {
  'a4-24': 'A4 - 24 per vel (63 x 30 mm), met briefpapier',
  'a4-65': 'A4 - 65 per vel (38 x 21 mm), etiketvel',
  'a4-sleutel': 'A4 - 66 per vel (30 x 22 mm), sleutellabel',
  // Kort houden: in een smalle modal bepaalt de langste optie de
  // minimumbreedte van het select-element. Het DK-rolnummer staat in de
  // printvenster-hint (LabelFormaatVeld), niet hier.
  'rol-62x29': 'Labelprinter 62 mm-rol - 62 x 29 mm',
  'rol-29x25': 'Labelprinter 29 mm-rol - 29 x 25, sleutel',
  'rol-29x15': 'Labelprinter 29 mm-rol - 29 x 15, sleutelhanger',
}

/**
 * Veldset per preset: welke tekst-indeling de renderer op het etiket gebruikt.
 * Eén bron voor de API-geometrie (`apps/api/src/lib/label-presets.ts`
 * consumeert deze map) én de backoffice-UI (welke inhoud-opties bij het
 * gekozen formaat zinvol zijn, zie `LABEL_OPTIES_PER_VELDSET`).
 */
export const LABEL_VELDSETS = ['volledig', 'compact', 'sleutel'] as const
export type LabelVeldset = (typeof LABEL_VELDSETS)[number]

export const LABEL_PRESET_VELDSET: Record<LabelPresetKey, LabelVeldset> = {
  'a4-24': 'volledig',
  'a4-65': 'compact',
  'a4-sleutel': 'sleutel',
  'rol-62x29': 'compact',
  'rol-29x25': 'sleutel',
  'rol-29x15': 'sleutel',
}

// ═════════════════════════════════════════════════════════════════════════
// Label-inhoud-opties — wat er op een etiket staat, per print-run instelbaar
// (tekstregels aan/uit + STREEP-logo). Gedeeld schema: het QR-PDF-endpoint
// parset ermee en de backoffice-checkboxes renderen erop. Weggelaten =
// exact het gedrag van vóór deze optie (per veldset, zie
// DEFAULT_LABEL_OPTIES_PER_VELDSET).
// ═════════════════════════════════════════════════════════════════════════

/**
 * Wire-payload: alle sleutels optioneel en bewust ZONDER `.default()` - de
 * server moet "weggelaten" kunnen onderscheiden van "bewust gekozen", omdat
 * een ontbrekende sleutel de VELDSET-default krijgt (compact: vaklocatie en
 * locatie uit) en een schema-default die veldset-basis zou overschrijven.
 */
export const labelOptiesSchema = z.object({
  logo: z.boolean().optional(),
  naam: z.boolean().optional(),
  identificatie: z.boolean().optional(),
  categorieMaat: z.boolean().optional(),
  vaklocatie: z.boolean().optional(),
  locatie: z.boolean().optional(),
  status: z.boolean().optional(),
  datum: z.boolean().optional(),
})
export type LabelOptiesInput = z.infer<typeof labelOptiesSchema>

/** Volledig opgeloste set: UI-checkbox-state en renderer-input. */
export interface LabelOpties {
  /** STREEP-logo op het etiket zelf (los van het briefpapier van a4-24). */
  logo: boolean
  /** Artikelnaam (of labelcode bij blanco labels) als kopregel. */
  naam: boolean
  /** Identificatieregel: kenteken/IMEI/serienummer/interne code (of labelcode). */
  identificatie: boolean
  /** Categorie + maat (alleen veldset 'volledig'). */
  categorieMaat: boolean
  /** Vaklocatie-regel "Vak 2-4" (veldsets 'volledig' en 'compact'). */
  vaklocatie: boolean
  /** Locatienaam, bv. "Hoofdmagazijn" (veldsets 'volledig' en 'compact'). */
  locatie: boolean
  /** Statusregel (alleen veldset 'volledig'). */
  status: boolean
  /** Datum-aangemaakt-footer (alleen veldset 'volledig'). */
  datum: boolean
}
export type LabelOptieKey = keyof LabelOpties

/** Alle tekst aan, geen logo: het a4-24-gedrag van vanouds. */
export const DEFAULT_LABEL_OPTIES: LabelOpties = {
  logo: false,
  naam: true,
  identificatie: true,
  categorieMaat: true,
  vaklocatie: true,
  locatie: true,
  status: true,
  datum: true,
}

/**
 * Startwaarden per veldset = exact het oude gedrag van dat formaat. De
 * compact-veldset printte vóór de inhoud-opties GEEN vaklocatie/locatienaam,
 * dus die staan daar default uit (opt-in - "moet erop kúnnen"); wie zonder
 * `labelOpties` print of de vinkjes niet aanraakt, ziet niets veranderen.
 * De renderer resolvet een binnenkomende payload over deze basis
 * (`resolveLabelOpties`), en de UI seedt zijn checkboxes hiermee bij een
 * veldset-wissel.
 */
export const DEFAULT_LABEL_OPTIES_PER_VELDSET: Record<LabelVeldset, LabelOpties> = {
  volledig: DEFAULT_LABEL_OPTIES,
  compact: { ...DEFAULT_LABEL_OPTIES, vaklocatie: false, locatie: false },
  sleutel: DEFAULT_LABEL_OPTIES,
}

/**
 * Resolve een (gedeeltelijke of afwezige) wire-payload naar de volledige
 * optie-set voor een veldset: ontbrekende sleutels krijgen de veldset-default.
 */
export function resolveLabelOpties(
  veldset: LabelVeldset,
  input?: LabelOptiesInput | null,
): LabelOpties {
  return { ...DEFAULT_LABEL_OPTIES_PER_VELDSET[veldset], ...(input ?? {}) }
}

export const LABEL_OPTIE_LABELS: Record<LabelOptieKey, string> = {
  logo: 'STREEP-logo',
  naam: 'Naam',
  identificatie: 'Code / nummer',
  categorieMaat: 'Categorie + maat',
  vaklocatie: 'Vaklocatie',
  locatie: 'Locatienaam',
  status: 'Status',
  datum: 'Datum',
}

/**
 * Welke opties op de veldset van het gekozen preset daadwerkelijk effect
 * hebben - de UI toont uitsluitend deze checkboxes (volgorde = UI-volgorde).
 * De renderer negeert opties buiten de veldset sowieso, dus een stale keuze
 * uit een ander preset kan nooit "stiekem" meeprinten.
 */
export const LABEL_OPTIES_PER_VELDSET: Record<LabelVeldset, readonly LabelOptieKey[]> = {
  volledig: [
    'naam',
    'categorieMaat',
    'identificatie',
    'vaklocatie',
    'locatie',
    'status',
    'datum',
    'logo',
  ],
  // Compact kent sinds de inhoud-opties óók vaklocatie + locatienaam ("Vak
  // 2-4" / "Hoofdmagazijn") - een magazijn-etiket op een kleine stans of rol
  // moet zijn plek kunnen tonen. Geen categorie/status/datum (te weinig ruimte).
  compact: ['naam', 'identificatie', 'vaklocatie', 'locatie', 'logo'],
  sleutel: ['identificatie', 'logo'],
}

// ═════════════════════════════════════════════════════════════════════════
// Blanco QR-labels (spec 2026-08-21 §4) — genereren, lijst, koppelen, vervallen.
// Werkwijze op de vloer: labelen-dan-koppelen (serie printen, plakken, pas
// daarna scannen en koppelen aan het exemplaar).
// ═════════════════════════════════════════════════════════════════════════

export const QR_LABEL_STATUS = ['ONGEBRUIKT', 'GEKOPPELD', 'VERVALLEN'] as const
export type QrLabelStatus = (typeof QR_LABEL_STATUS)[number]

export const QR_LABEL_STATUS_LABELS: Record<QrLabelStatus, string> = {
  ONGEBRUIKT: 'Ongebruikt',
  GEKOPPELD: 'Gekoppeld',
  VERVALLEN: 'Vervallen',
}

export interface QrLabelDTO {
  id: string
  token: string
  code: string
  serie: string | null
  status: QrLabelStatus
  uniekItemId: string | null
  /** Korte omschrijving van het gekoppelde exemplaar, voor de lijst. */
  uniekItemOmschrijving: string | null
  gekoppeldOp: string | null
  vervallenReden: string | null
  createdAt: string
}

export interface QrLabelSerieDTO {
  serie: string | null
  aantal: number
  vanCode: string
  totCode: string
  labelIds: string[]
}

export const genereerQrLabelsSchema = z.object({
  aantal: z.number().int().min(1).max(500),
  serie: z.string().trim().min(1).max(40).optional().nullable(),
})
export type GenereerQrLabelsInput = z.infer<typeof genereerQrLabelsSchema>

export const qrLabelListQuerySchema = z.object({
  status: z.enum(QR_LABEL_STATUS).optional(),
  /** Serienaam-filter: substring en case-insensitief, tenzij `serieExact`. */
  serie: z.string().trim().min(1).max(40).optional(),
  /**
   * true = `serie` exact matchen. Voor de "Labels"-knop bij een serie-rij: het
   * naamschema zelf maakt prefix-collisies ("2026-09-A" naast "2026-09-AA"),
   * dus een substring-match zou daar labels van een ándere serie tonen en de
   * rijtelling tegenspreken. Querystring-vorm 'true'/'false' toegestaan.
   */
  serieExact: z
    .union([z.boolean(), z.enum(['true', 'false'])])
    .optional()
    .transform((v) => v === true || v === 'true'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})

/**
 * Serie-overzicht (GET /voorraad/qr/labels/series): één rij per benoemde serie
 * met tellingen, zodat de gebruiker een serie kíést (en print) in plaats van de
 * naam exact over te typen. Labels zonder serienaam vallen hier bewust buiten:
 * die zijn per serie toch niet printbaar (alleen via labelIds direct na
 * genereren) en blijven gewoon zichtbaar in de labels-lijst.
 */
export interface QrLabelSerieOverzichtDTO {
  serie: string
  totaal: number
  ongebruikt: number
  gekoppeld: number
  vervallen: number
  /** ISO-datum van het nieuwste label in de serie (sorteersleutel, nieuwste eerst). */
  laatstAangemaakt: string
}

export const qrLabelSerieListQuerySchema = z.object({
  /** Substring-zoek (case-insensitief) op de serienaam. */
  search: z.string().trim().min(1).max(40).optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(50).default(10),
})

export interface QrLabelSerieListMeta {
  total: number
  page: number
  pageSize: number
  /**
   * Voorstel voor de volgende serienaam ("2026-09-A"): jaar-maand in
   * Europe/Amsterdam plus de eerste vrije letter. Vult het genereer-formulier
   * voor, zodat niemand zelf een naamconventie hoeft te verzinnen.
   * Alleen aanwezig op pagina 1 zonder zoekterm (daar leest de UI hem); zo
   * rekent niet elke paginaklik of toetsaanslag het voorstel opnieuw uit.
   */
  serieSuggestie?: string
}

export const koppelQrLabelSchema = z.object({
  uniekItemId: z.string().min(1),
})

export const vervalQrLabelSchema = z.object({
  reden: z.string().trim().min(1).max(500),
})

// ═════════════════════════════════════════════════════════════════════════
// Blanco label → autosleutel (scanner-koppelflow, 2026-09-01).
// De monteur kiest een voertuig + hoofd/reserve; de API koppelt aan de
// bestaande ongelabelde sleutel van die soort of maakt het exemplaar aan
// (SL-reeks, spiegel van de sleutel-uitrol §7).
// ═════════════════════════════════════════════════════════════════════════

export const koppelSleutelLabelSchema = z.object({
  voertuigId: z.string().min(1),
  isReserve: z.boolean(),
  /**
   * Sleutelkast-locatie voor een NIEUW aan te maken sleutel. Optioneel: zonder
   * waarde leidt de API de default af (zelfde afleiding als het context-
   * endpoint). Genegeerd wanneer er aan een bestaande sleutel gekoppeld wordt.
   */
  locatieId: z.string().min(1).optional().nullable(),
  /**
   * AUTOSLEUTEL-artikel voor een NIEUW exemplaar (de UI stuurt de waarde uit
   * het context-endpoint door). Optioneel: zonder waarde resolvet de API zelf
   * het oudste actieve traceerbare AUTOSLEUTEL-artikel. De API valideert de
   * waarde altijd (bestaand + AUTOSLEUTEL + traceerbaar).
   */
  artikelId: z.string().min(1).optional().nullable(),
})
export type KoppelSleutelLabelInput = z.infer<typeof koppelSleutelLabelSchema>

/** Eén bestaande autosleutel van een voertuig, voor de keuzekaarten. */
export interface SleutelKoppelSleutelDTO {
  id: string
  /** SL-nummer (assetTag), null bij een handmatig exemplaar zonder tag. */
  nummer: string | null
  isReserve: boolean
  status: VoorraadUniekStatus
  /** Code van het actieve GEKOPPELD-label, null = nog niet gelabeld. */
  labelCode: string | null
}

/** Uitkomst-voorschouw voor één soort (hoofd/reserve) op een voertuig. */
export type SleutelSoortVoorschouw =
  | { kan: true; actie: 'BESTAAND'; sleutelId: string; nummer: string | null }
  | { kan: true; actie: 'NIEUW' }
  | { kan: false; gelabeld: Array<{ nummer: string | null; labelCode: string | null }> }

/**
 * Wat er bij koppelen gaat gebeuren voor een soort (hoofd/reserve). Eén bron
 * voor de portal-UI (keuzekaarten) én het schrijfpad in de API
 * (`koppelQrLabelAanSleutel`): AFGESCHREVEN telt niet mee (weg is weg), de
 * oudste sleutel zonder actief label wint, en zijn alle sleutels van die soort
 * al gelabeld dan kan er niet gekoppeld worden (labelvervanging loopt via
 * vervallen-verklaren). De input moet oudste-eerst gesorteerd zijn; beide
 * dataleveranciers sorteren op `createdAt asc`.
 */
export function bepaalSleutelSoortVoorschouw(
  sleutels: ReadonlyArray<
    Pick<SleutelKoppelSleutelDTO, 'id' | 'nummer' | 'isReserve' | 'status' | 'labelCode'>
  >,
  isReserve: boolean,
): SleutelSoortVoorschouw {
  const kandidaten = sleutels.filter(
    (s) => s.isReserve === isReserve && s.status !== 'AFGESCHREVEN',
  )
  const zonderLabel = kandidaten.find((s) => s.labelCode == null)
  if (zonderLabel) {
    return { kan: true, actie: 'BESTAAND', sleutelId: zonderLabel.id, nummer: zonderLabel.nummer }
  }
  if (kandidaten.length > 0) {
    return {
      kan: false,
      gelabeld: kandidaten.map((s) => ({ nummer: s.nummer, labelCode: s.labelCode })),
    }
  }
  return { kan: true, actie: 'NIEUW' }
}

/** Voertuig-zoekresultaat in de sleutel-koppelflow. */
export interface SleutelKoppelVoertuigDTO {
  id: string
  kenteken: string
  merk: string
  model: string
  /** Actieve bestuurder (VoertuigToewijzing), voor "wordt uitgegeven aan". */
  bestuurderNaam: string | null
  /** Alle actieve autosleutels van dit voertuig (oudste eerst, per soort). */
  sleutels: SleutelKoppelSleutelDTO[]
}

/** Eenmalige context voor de sleutel-koppelflow (locaties + defaults). */
export interface SleutelKoppelContextDTO {
  /** Actieve voorraadlocaties waar een nieuwe reserve op voorraad kan komen. */
  locaties: Array<{ id: string; naam: string }>
  /** Afgeleide sleutelkast-default, null wanneer er geen locatie bestaat. */
  defaultLocatieId: string | null
  /**
   * Het traceerbare AUTOSLEUTEL-artikel waarop nieuwe exemplaren landen (de UI
   * stuurt dit door in de koppel-call). Null = er bestaat er geen; een nieuwe
   * sleutel aanmaken kan dan niet.
   */
  artikelId: string | null
}

/** Resultaat van POST /voorraad/qr/labels/:token/koppel-sleutel. */
export interface KoppelSleutelLabelResultDTO {
  label: QrLabelDTO
  sleutel: {
    id: string
    nummer: string | null
    isReserve: boolean
    /** true = exemplaar is in deze call aangemaakt; false = bestaand gekoppeld. */
    aangemaakt: boolean
  }
  /**
   * Houder van de sleutel ná deze call: de bestuurder aan wie een nieuwe
   * hoofdsleutel is uitgegeven, of (idempotente herhaling) de huidige houder.
   * Null wanneer de sleutel op voorraad staat.
   */
  uitgegevenAanNaam: string | null
  /**
   * Niet-blokkerende afwijking: uitgifte aan de bestuurder mislukt, of (bij
   * een idempotente herhaling) een hoofdsleutel die nog op voorraad staat
   * terwijl het voertuig een actieve bestuurder heeft.
   */
  waarschuwing: string | null
}
